import React, { useState, useEffect, useMemo } from 'react';
import styles from './style.module.less';
import { Image } from "antd";
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme, THEME_LIGHT, THEME_DARK, THEME_SYSTEM } from '@/contexts/ThemeContext';

function Header({ data }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('');
    const { themeMode, resolvedTheme, setThemeMode } = useTheme();
    const [themeMenuOpen, setThemeMenuOpen] = useState(false);

    // 由接口数据生成导航项，无数据时回退到默认配置
    const navList = useMemo(() => {
        if (data && data.length > 0) {
            return data.map((item) => ({
                key: item.blockCode || item.id,
                title: item.title,
                url: item.url,
                buttons: item.button,
            }));
        }
        return [];
    }, [data]);

    // 收集参与滚动监听的锚点 id（url 以 # 开头的项）
    const sectionIds = useMemo(() => navList
      .map((item) => (item.url && typeof item.url === 'string' && item.url.startsWith('#') ? item.url.slice(1) : null))
      .filter(Boolean), [navList]);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 100;
            for (let i = sectionIds.length - 1; i >= 0; i--) {
                const section = document.getElementById(sectionIds[i]);
                if (section && section.offsetTop <= scrollPosition) {
                    setActiveTab(sectionIds[i]);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [sectionIds]);

    // 点击主题切换区域外部时关闭下拉
    useEffect(() => {
        if (!themeMenuOpen) return undefined;
        const handleClickOutside = (e) => {
            if (!e.target.closest(`.${styles.themeToggle}`)) {
                setThemeMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [themeMenuOpen]);

    const handleUrlClick = (url) => {
        if (!url) return;
        if (url.startsWith('#')) {
            const id = url.slice(1);
            // 当前不在 home 页面时，先跳转到 home 页面，再由 home 根据 state 滚动到对应锚点
            if (location.pathname !== '/') {
                navigate('/');
                //加一个延迟
                setTimeout(() => {
                    const target = document.getElementById(id);
                    if (target) {
                        window.scrollTo({
                            top: target.getBoundingClientRect().top + window.scrollY - 64,
                            behavior: 'smooth',
                        });
                        setActiveTab(id);
                    }
                }, 500)
                return;
            }
            const target = document.getElementById(id);
            if (target) {
                window.scrollTo({
                    top: target.getBoundingClientRect().top + window.scrollY - 64,
                    behavior: 'smooth',
                });
                setActiveTab(id);
            }
        } else if (url.startsWith('http')) {
            setActiveTab(url);
            window.open(url, '_blank');
        } else {
            setActiveTab(url);
            navigate(url);
        }
    };

    return (
      <header className={ styles.header }>
          <div className={ styles.content }>
              <div className={ styles.logo } onClick={ () => {
                  navigate("/")
              } }>
                  <div className={ styles.logoIcon }>
                      <img src={ resolvedTheme === THEME_DARK?"./logo-light.svg": "./logo.svg" }  height={ 40 } alt={""}/>
                  </div>
              </div>

              <nav className={ styles.navMenu }>
                  { navList.map((item) => {
                      const hasButtons = item.buttons && item.buttons.length > 0;
                      const isAnchor = Boolean(item.url && item.url.startsWith('#'));
                      // home 页不判断路由，仅锚点项按滚动位置高亮；其他页按路由与菜单 url 匹配高亮
                      let isActive;
                      if (location.pathname === '/') {
                          isActive = isAnchor && activeTab === item.url.slice(1);
                      } else {
                          isActive = !isAnchor && location.pathname === item.url;
                      }

                      if (hasButtons) {
                          return (
                            <div key={ item.key } className={ `${ styles.navItem } ${ styles.hasDropdown }` }>
                                <span className={ styles.navTitle }>{ item.title }</span>
                                <div className={ styles.dropdown }>
                                    <div className={ styles.dropdownPanel }>
                                        { item.buttons.map((btn) => (
                                          <a
                                            key={ btn.title }
                                            href={ btn.url || '#' }
                                            className={ styles.dropdownItem }
                                            onClick={ (e) => {
                                                e.preventDefault();
                                                handleUrlClick(btn.url);
                                            } }
                                          >
                                              <div className={ styles.dropdownText }>
                                                  <span className={ styles.dropdownTitle }>{ btn.title }</span>
                                                  <span className={ styles.dropdownSubtitle }>{ btn.subtitle }</span>
                                              </div>
                                          </a>
                                        )) }
                                    </div>
                                </div>
                            </div>
                          );
                      }

                      return (
                        <a
                          key={ item.key }
                          href={ item.url || '#' }
                          className={ `${ styles.navItem } ${ isActive ? styles.active : '' }` }
                          onClick={ (e) => {
                              e.preventDefault();
                              handleUrlClick(item.url);
                          } }
                        >
                            { item.title }
                        </a>
                      );
                  }) }
              </nav>

              <div className={ styles.themeToggle }>
                  <button
                    type="button"
                    className={ styles.themeBtn }
                    onClick={ () => setThemeMenuOpen((v) => !v) }
                    aria-label="切换主题"
                  >
                      { resolvedTheme === THEME_DARK ? (
                        // 月亮图标
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                        </svg>
                      ) : (
                        // 太阳图标
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="5"/>
                            <line x1="12" y1="1" x2="12" y2="3"/>
                            <line x1="12" y1="21" x2="12" y2="23"/>
                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                            <line x1="1" y1="12" x2="3" y2="12"/>
                            <line x1="21" y1="12" x2="23" y2="12"/>
                            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                        </svg>
                      ) }
                  </button>
                  { themeMenuOpen && (
                    <div className={ styles.themeDropdown }>
                      { [
                          { mode: THEME_LIGHT, label: '亮色', icon: (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="5"/>
                                  <line x1="12" y1="1" x2="12" y2="3"/>
                                  <line x1="12" y1="21" x2="12" y2="23"/>
                                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                                  <line x1="1" y1="12" x2="3" y2="12"/>
                                  <line x1="21" y1="12" x2="23" y2="12"/>
                                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                              </svg>
                          ) },
                          { mode: THEME_DARK, label: '暗色', icon: (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                              </svg>
                          ) },
                          { mode: THEME_SYSTEM, label: '跟随系统', icon: (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                                  <line x1="8" y1="21" x2="16" y2="21"/>
                                  <line x1="12" y1="17" x2="12" y2="21"/>
                              </svg>
                          ) },
                      ].map((opt) => (
                        <button
                          key={ opt.mode }
                          type="button"
                          className={ `${ styles.themeOption } ${ themeMode === opt.mode ? styles.themeOptionActive : '' }` }
                          onClick={ () => {
                              setThemeMode(opt.mode);
                              setThemeMenuOpen(false);
                          } }
                        >
                            <span className={ styles.themeOptionIcon }>{ opt.icon }</span>
                            <span className={ styles.themeOptionLabel }>{ opt.label }</span>
                            { themeMode === opt.mode && (
                              <svg className={ styles.themeOptionCheck } width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"/>
                              </svg>
                            ) }
                        </button>
                      )) }
                    </div>
                  ) }
              </div>
          </div>
      </header>
    );
}

export default Header;
